import { HgSupportedLanguage } from '@smg-real-estate/domus';
import slugify from 'slugify';

import type { AgentProfile } from '@/types/agentProfile';

/** Locale-specific agency directory home route name (e.g. home-de). */
export function getHomeRouteName(locale: string): string {
  return `home-${locale}`;
}

/** Locale-specific agent directory home route name (e.g. agent-home-de). */
export function getAgentHomeRouteName(locale: string): string {
  return `agent-home-${locale}`;
}

export function isAgentHomeRouteName(routeName: string): boolean {
  return routeName.startsWith('agent-home-');
}

export const generateAgencyProfilePagePath = (
  language: HgSupportedLanguage,
  agencyUrlKeyword: string,
  agencyId: string,
): string => {
  const agencyProfilePageRoutes: Record<HgSupportedLanguage, string> = {
    [HgSupportedLanguage.DE]: 'anbieter',
    [HgSupportedLanguage.EN]: 'agency',
    [HgSupportedLanguage.FR]: 'regie',
    [HgSupportedLanguage.IT]: 'agenzia',
  };

  return `${import.meta.env.VUE_APP_BASE_URL}/${agencyProfilePageRoutes[language]}/${agencyId}/${agencyUrlKeyword}`;
};

/**
 * Agent profile page (PDP) route prefixes, ported from agency-profile-vue's
 * agentProfileRoutingHelpers.ts — the agent PDP is served by that app.
 */
export const AGENT_PROFILE_PAGE_ROUTE_PREFIXES: Record<HgSupportedLanguage, string> = {
  [HgSupportedLanguage.DE]: 'de/makler',
  [HgSupportedLanguage.EN]: 'en/agent',
  [HgSupportedLanguage.FR]: 'fr/agent',
  [HgSupportedLanguage.IT]: 'it/agente',
};

function slugifySegment(segment: string): string {
  return slugify(segment, {
    lower: true,
    strict: true,
    trim: true,
  });
}

/** Slug shape: `{firstName}-{lastName}-{city}-{agentId}` (agentId is always the last segment). */
export function createCanonicalAgentSlug({
  firstName,
  lastName,
  city,
  agentId,
}: {
  firstName: string;
  lastName: string;
  city?: string | null;
  agentId: number;
}): string {
  const nameAndCitySegments = [firstName, lastName, city || ''].map(slugifySegment).filter(Boolean);

  return [...nameAndCitySegments, agentId].join('-');
}

export const generateAgentProfilePagePath = (
  language: HgSupportedLanguage,
  agent: Pick<AgentProfile, 'agentId' | 'firstName' | 'lastName' | 'cityLocation'>,
): string => {
  const agentSlug = createCanonicalAgentSlug({
    firstName: agent.firstName,
    lastName: agent.lastName,
    city: agent.cityLocation,
    agentId: agent.agentId,
  });

  return `${import.meta.env.VUE_APP_BASE_URL}/${AGENT_PROFILE_PAGE_ROUTE_PREFIXES[language]}/${agentSlug}`;
};
